import React, { useEffect, useState } from 'react';

//context
import SearchLandingProvider from '@/context/SearchLandingContext';

//hooks
import useDebounce from '../../hooks/useDebounce';
import useHTTP from  '@/components/hooks/useHTTP';

//components
import SearchFilterButton from './searchFilterButton';
import SearchFilterMultiSelectButton from './searchFilterMultiSelectButton';
import RecentlySearched from './recentlySearched';
import RecentItemsTabList from './recentItemsTabList';

//interfaces
import { ContentSearchRequest, SearchFilters } from './interfaces/Search';
import { IconLibrary, SimpleSearchInput, ThriveIcon } from 'thrive-components';



enum FilterKey {
    searchTerm='searchTerm',
    typeFilter='typeFilter', 
    dateFilter='dateFilter',
    rulesFilter='rulesFilter', 
    languageFilter='languageFilter'
}

interface Language {
    ietfLanguageTag: string,
    iso3166RegionCode: string,
    nativeDisplayName: string
}

interface SearchControlsProps {
    getResults: (searchTerm: string, typeFilter: string[], dateFilter:string, languageFilter: string[], rulesFilter: string[]) => void,
    totalResults: number,
    defaultFilters: SearchFilters,
    setFilters: (filters: SearchFilters) => void
    appName: string
    setLastSearchTerms: (lastSearch: ContentSearchRequest) => void
}


const SearchControls = ({getResults, totalResults, defaultFilters, setFilters, appName, setLastSearchTerms}: SearchControlsProps ) => {
    const {searchTerm, typeFilter, dateFilter, rulesFilter, languageFilter} = defaultFilters as SearchFilters
   
    const [languageOptions, setLanguageOptions] = useState<any[]>([]);
    const [fetchingLanguages, setFetchingLanguages] = useState<boolean>(true) 
    const [lastSearchTerm, setLastSearchTerm] = useState<string>('');
    const [searchDirty, setSearchDirty] = useState<boolean>(false);

    const debouncedSearchTerm = useDebounce(searchTerm, 500)
    const { get } = useHTTP();


    useEffect(() => {
        get('/language/enabled')
            .then(({data}:{data:{languages: Language[]}}) => {
                /** Sanitise response and remove duplicate ietfLanguageTags **/
                let languageOptions = [];
                let languageTags: string[] = [];

                for (const language of data.languages) {
                    if(!languageTags.includes(language.ietfLanguageTag)) {
                      languageOptions.push(language);
                      languageTags.push(language.ietfLanguageTag);
                    }
                }

                setLanguageOptions(languageOptions.map((lang: Language) => getOptionsFromCode(lang.ietfLanguageTag, lang.iso3166RegionCode, lang.nativeDisplayName)).filter(option => option));
            })
            .finally(() => setFetchingLanguages(false));
    },[])

    function setFilterValue(key: FilterKey, value: string | string[]) {
        setFilters({...defaultFilters, [key]: value})
    }

    function getOptionsFromCode(tag: string, code: string, nativeLanguage: string) {
        if (!code) {
            return false;
        }

        function getFlagEmoji(code:string) {
            const codePoints = code
              .toUpperCase()
              .split('')
              .map((char: string) =>  127397 + char.charCodeAt(0));
            return String.fromCodePoint(...codePoints);
        }

        return {label: <><span className='country-flag'>{getFlagEmoji(code)}</span> {nativeLanguage}</>, value: tag}
    }
    
    useEffect(() => {   
        if(searchTerm) {
            getResults(searchTerm, typeFilter, dateFilter, languageFilter, rulesFilter);
        }
    }, [typeFilter.length, dateFilter, languageFilter.length, rulesFilter.length])

    useEffect(() => {
        setLastSearchTerm(debouncedSearchTerm);
        if(searchTerm && debouncedSearchTerm !== lastSearchTerm) { 
            getResults(debouncedSearchTerm, typeFilter, dateFilter, languageFilter, rulesFilter);
        }
    },[debouncedSearchTerm])

    useEffect(() => {
        if(searchTerm) {
            setSearchDirty(true);
        }
    }, [searchTerm])
    
    return (<div>
        <SimpleSearchInput 
            ariaLabel={`Search for any content across ${appName}`}
            setSearchString={(inputValue) => setFilterValue(FilterKey.searchTerm, inputValue)} 
            searchString={searchTerm} 
            placeholder={`Search for any content across ${appName}`}
            className='search-input-for-search-modal'
        />

        {!searchTerm && 
            <div className='search-landing-container'>
                <SearchLandingProvider setLastSearchTerms={setLastSearchTerms} searchDirty={searchDirty} setSearchDirty={setSearchDirty}>
                    <RecentlySearched clickSearchTerm={(text:string) => setFilterValue(FilterKey.searchTerm, text)}/>
                    <RecentItemsTabList />
                </SearchLandingProvider>
            </div>
        }
        {!!searchTerm &&  <div className='search-control-container'>
            <div className='filterWrapper'>
                <SearchFilterButton 
                    id='datetime'
                    key='datetime'
                    icon={<ThriveIcon iconName={IconLibrary.CALENDAR} sizeInPx='9' />}
                    setValue={(value: string) => setFilterValue(FilterKey.dateFilter, value)}
                    buttonLabel={'Date'}
                    options={['Last 7 Days', 'Last 30 Days', 'Last 6 Months', 'Past Year']}
                    defaultValue={dateFilter}
                    canDeselect
                />
                
                <SearchFilterMultiSelectButton
                    name={'Content Type'}
                    id='filetype'
                    key='fileType'
                    icon={<ThriveIcon iconName={IconLibrary.LIST_ITEM} sizeInPx='9' />}
                    defaultValues={[]}
                    selectedValues={typeFilter}
                    options={[
                        {label: 'Images Only', value:'image'},
                        {label: 'Video Only', value: 'video'},
                        {label: 'Files Only', value: 'file'},
                        {label: 'Audio Only', value: 'audio'},
                        {label: 'Web Only', value: 'web'}
                    ]}
                    setValue={(value: string[]) => setFilterValue(FilterKey.typeFilter, value)}
                />
        
                <SearchFilterMultiSelectButton
                    name={'Rules'}
                    id='rules'
                    key='rules'
                    icon={<ThriveIcon iconName={IconLibrary.COG} sizeInPx='9' />}
                    defaultValues={[]}
                    selectedValues={rulesFilter}
                    options={[
                        {label: 'Show Published Only', value:'CS_Published'},
                        {label: 'Show Drafts Only', value:'CS_Draft', split:true},
                        {label: 'Search Titles Only', value:'SearchTitlesOnly'},
                    ]}
                    setValue={(value: string[]) => setFilterValue(FilterKey.rulesFilter, value)}
                    hasAlwaysOnOptions
                />
                    
                {!!languageOptions.length && 
                    <SearchFilterMultiSelectButton
                        name={'Languages'}
                        id='language'
                        key='language'
                        icon={<ThriveIcon iconName={IconLibrary.GLOBE} sizeInPx='9' />}
                        selectedValues={languageFilter}
                        defaultValues={[]}
                        options={languageOptions}
                        setValue={(value: string[]) => setFilterValue(FilterKey.languageFilter, value)}
                        isLoading={fetchingLanguages}
                    />
                }
            </div>

            {(!!totalResults && searchTerm) && 
                <span className='no-of-results'>
                    {totalResults === 1 
                        ? '1 match' 
                        : `${totalResults} matches`
                    }
                </span>
            }
       </div>}
        
    </div>)
}

export default SearchControls;